import type { Dayjs } from "dayjs";
import moment from "moment-timezone";
import { getSlotRulesFromStore } from "../../../services/utils/date-and-time.utils";
import {
  isLateNightInTimezone,
  isShortNoticeInTimezone,
  isWeekendInTimezone,
} from "../../../screens/project-details/utils";
import type { TimeSlotMeta } from "./utils";
import { buildSlotSelectionPayload } from "./utils";

const AMERICA_NEW_YORK = "America/New_York";

export type SlotCriteriaResult = {
  allValid: boolean;
  isWeekend: boolean;
  isShortNotice: boolean;
  isLateNight: boolean;
  oppositeDaytimeOk: boolean | null;
};

export const getEndDateTimeForSlotRange = (
  rawDate: Date,
  rawStartTime: Date,
  rawEndTime: Date
) => {
  const startHour = moment(rawStartTime).hour();
  const startMinute = moment(rawStartTime).minute();
  const endHour = moment(rawEndTime).hour();
  const endMinute = moment(rawEndTime).minute();
  const isNextDay =
    endHour < startHour || (endHour === startHour && endMinute <= startMinute);
  const endDateTime = moment(rawDate)
    .hour(endHour)
    .minute(endMinute)
    .second(0)
    .millisecond(0);
  if (isNextDay) {
    endDateTime.add(1, "day");
  }
  return endDateTime;
};

export function evaluateSlotSelectionCriteria(
  selectedDate: Dayjs,
  slots: TimeSlotMeta[],
  fromIndex: number,
  toIndex: number | null,
  oppositeUserTimezone: string | null
): SlotCriteriaResult {
  const payload = buildSlotSelectionPayload(
    selectedDate,
    slots,
    fromIndex,
    toIndex
  );

  const rawDate = payload.date;
  const rawStart = payload.startTime;
  const rawEnd = payload.endTime;
  const isRangeSelection = toIndex !== null;

  const selectedStartDateTime = moment(rawDate)
    .hour(moment(rawStart).hour())
    .minute(moment(rawStart).minute())
    .second(0)
    .millisecond(0);

  const etStartDateTime = selectedStartDateTime
    .clone()
    .tz(AMERICA_NEW_YORK);

  let etEndDateTime: moment.Moment | null = null;
  if (isRangeSelection) {
    etEndDateTime = getEndDateTimeForSlotRange(
      rawDate,
      rawStart,
      rawEnd
    ).tz(AMERICA_NEW_YORK);
  }

  const startDateTimeForValidation = etStartDateTime.toISOString();
  const { allowWeekends } = getSlotRulesFromStore();
  const isCalendarWeekendEt = isWeekendInTimezone(
    startDateTimeForValidation,
    AMERICA_NEW_YORK
  );
  const isWeekend = allowWeekends ? false : isCalendarWeekendEt;
  const isShortNotice = isShortNoticeInTimezone(
    startDateTimeForValidation,
    AMERICA_NEW_YORK
  );
  const { minHour, maxHour } = getSlotRulesFromStore().businessHours;
  const startMinutesEt =
    etStartDateTime.hour() * 60 + etStartDateTime.minute();
  const minMinutesEt = minHour * 60;
  const maxMinutesEt = maxHour * 60;
  const SLOT_DURATION_MINUTES = 45;
  const isLateNightStart =
    startMinutesEt < minMinutesEt ||
    startMinutesEt + SLOT_DURATION_MINUTES > maxMinutesEt;

  let isLateNightEnd = false;
  let spansOvernight = false;
  if (isRangeSelection && etEndDateTime) {
    const endDateTimeForValidation = etEndDateTime.toISOString();
    isLateNightEnd = isLateNightInTimezone(
      endDateTimeForValidation,
      AMERICA_NEW_YORK
    );
    const startDateInET = etStartDateTime.format("YYYY-MM-DD");
    const endDateInET = etEndDateTime.format("YYYY-MM-DD");
    spansOvernight = startDateInET !== endDateInET;
  }

  const isLateNight = isLateNightStart || isLateNightEnd || spansOvernight;

  let oppositeDaytimeOk: boolean | null = null;
  if (oppositeUserTimezone) {
    let isLateNightStartOpp = isLateNightInTimezone(
      selectedStartDateTime.clone().tz(oppositeUserTimezone).toISOString(),
      oppositeUserTimezone
    );
    let isLateNightEndOpp = false;
    let spansOvernightOpp = false;
    if (isRangeSelection) {
      const selectedEndDateTime = getEndDateTimeForSlotRange(
        rawDate,
        rawStart,
        rawEnd
      );
      const convertedEndDateTime = selectedEndDateTime.clone().tz(oppositeUserTimezone);
      isLateNightEndOpp = isLateNightInTimezone(
        convertedEndDateTime.toISOString(),
        oppositeUserTimezone
      );
      const startD = selectedStartDateTime
        .clone()
        .tz(oppositeUserTimezone)
        .format("YYYY-MM-DD");
      const endD = convertedEndDateTime.format("YYYY-MM-DD");
      spansOvernightOpp = startD !== endD;
    }
    const isLateNightOpp =
      isLateNightStartOpp || isLateNightEndOpp || spansOvernightOpp;
    oppositeDaytimeOk = !isLateNightOpp;
  }

  const nyOk = !isWeekend && !isShortNotice && !isLateNight;
  const allValid = nyOk;

  return {
    allValid,
    isWeekend,
    isShortNotice,
    isLateNight,
    oppositeDaytimeOk,
  };
}
